import React, { useState, useEffect } from "react";
import { memo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Header from "./Includes/Header";
import DashboardHeader from "./Includes/DashboardHeader";
import AdminHeader from "./Includes/AdminHeader";
import Footer from "./Includes/Footer";

/* ── Professional inline SVG icons ── */
const Svg = ({ children, size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

/* Mission card icons */
const IcRegistry    = (p) => <Svg {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8M8 11h8M8 15h5"/></Svg>;
const IcIdCard      = (p) => <Svg {...p}><rect x="2" y="5" width="20" height="14" rx="2"/><circle cx="8" cy="12" r="2"/><path d="M14 9h4M14 12h4M14 15h2"/></Svg>;
const IcPieChart    = (p) => <Svg {...p}><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></Svg>;
const IcZap         = (p) => <Svg {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Svg>;

/* Partnership icons */
const IcBuilding2   = (p) => <Svg {...p}><path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"/><path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"/><path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"/><path d="M10 6h4M10 10h4M10 14h4M10 18h4"/></Svg>;
const IcRocket      = (p) => <Svg {...p}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></Svg>;
const IcScale       = (p) => <Svg {...p}><line x1="12" y1="3" x2="12" y2="21"/><path d="M3 9l9-6 9 6"/><path d="M3 9l4 8H3l4-8zM17 9l4 8h-8l4-8z"/></Svg>;
const IcShieldCheck = (p) => <Svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></Svg>;

/* Leadership icons */
const IcUser        = (p) => <Svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Svg>;
const IcCode        = (p) => <Svg {...p}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></Svg>;

/* Contact icons */
const IcMapPin      = (p) => <Svg {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Svg>;
const IcMail        = (p) => <Svg {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Svg>;
const IcGlobe       = (p) => <Svg {...p}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Svg>;

// Mobile breakpoint hook
const useIsMobile = (bp = 768) => {
  const [m, setM] = React.useState(() => window.innerWidth <= bp);
  useEffect(() => {
    const h = () => setM(window.innerWidth <= bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const About = () => {
  const { t } = useTranslation();
  const [header, setHeader] = useState("guest");
  const isMobile = useIsMobile();
  const { auth } = useSelector((state) => state);

  useEffect(() => {
    if (auth.data) {
      const response = auth.data;
      if (response.status === 1 && response.user) {
        setHeader(response.user.role !== 1 ? "dashboard" : "admin");
      } else {
        setHeader("guest");
      }
    }
  }, [auth]);

  const cardStyle = {
    background: "#f4f4f2", borderRadius: "12px", padding: "24px 20px", borderTop: "3px solid #c9a84c"
  };
  const sectionLabel = {
    color: "#c9a84c", fontWeight: 600, letterSpacing: "2px", fontSize: "12px",
    textTransform: "uppercase", marginBottom: "12px"
  };
  const sectionH2 = {
    fontFamily: "'DM Serif Display', serif", fontSize: "clamp(28px, 3vw, 38px)",
    color: "#0e3725", margin: "0 0 24px", fontWeight: 400
  };

  const missionCards = [
    { Ic: IcRegistry,  title: t("about.rerIntegrated"),      desc: t("about.rerIntegratedDesc") },
    { Ic: IcIdCard,    title: t("about.nafathKyc"),           desc: t("about.nafathKycDesc") },
    { Ic: IcPieChart,  title: t("about.fractionalOwnership"), desc: t("about.fractionalOwnershipDesc") },
    { Ic: IcZap,       title: t("about.instantSettlement"),   desc: t("about.instantSettlementDesc") },
  ];

  const partnerships = [
    { name: "Real Estate Registry (RER)", role: t("about.partnerRer"),  Ic: IcBuilding2 },
    { name: "Alinma AmadTech",            role: t("about.partnerAmaad"),          Ic: IcRocket },
    { name: "REGA Sandbox",               role: t("about.partnerRega"),        Ic: IcScale },
    { name: "Nafath",                     role: t("about.partnerNafath"),       Ic: IcShieldCheck },
  ];

  const contactItems = [
    { Ic: IcMapPin, label: t("contact.address"),      value: "7076 Al Sahaba Rd., Ishbiliyah 13225, Riyadh, Saudi Arabia" },
    { Ic: IcMail,   label: t("contact.emailLabel"), value: "partnerships@treasury.sa" },
    { Ic: IcGlobe,  label: t("common.website"),      value: "platform.treasury.sa" },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#f4f4f2" }}>
      {header === "guest" ? <Header /> : header === "dashboard" ? <DashboardHeader /> : <AdminHeader />}
      <div className="main_content">

        {/* Hero */}
        <section className="hero-section" style={{ background: "linear-gradient(135deg, #0e3725 0%, #1a5c3a 60%, #0e3725 100%)", padding: "80px 0 60px", textAlign: "center", color: "#fff" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "0 24px" }}>
            <p style={sectionLabel}>{t("about.whoWeAre")}</p>
            <h1 className="hero-title" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 600, margin: "0 0 20px", lineHeight: 1.15 }}>
              {t("about.heroTitle")}
            </h1>
            <p style={{ fontSize: "18px", color: "rgba(255,255,255,0.82)", lineHeight: 1.7, maxWidth: "640px", margin: "0 auto" }}>
              {t("about.heroSubtitle")}
            </p>
          </div>
        </section>

        {/* Mission and Vision */}
        <section style={{ background: "#fff", padding: "72px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px", display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "32px" : "60px", alignItems: "center" }}>
            <div>
              <p style={sectionLabel}>{t("about.ourMission")}</p>
              <h2 style={sectionH2}>{t("about.missionTitle")}</h2>
              <p style={{ color: "#555", lineHeight: 1.8, fontSize: "16px", marginBottom: "16px" }}>
                {t("about.missionP1")}
              </p>
              <p style={{ color: "#555", lineHeight: 1.8, fontSize: "16px" }}>
                {t("about.missionP2")}
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "20px" }}>
              {missionCards.map((item, i) => (
                <div key={i} style={cardStyle}>
                  <div style={{ marginBottom: "10px" }}>
                    <item.Ic size={28} color="#0e3725" />
                  </div>
                  <h4 style={{ color: "#0e3725", fontSize: "15px", fontWeight: 700, margin: "0 0 8px" }}>{item.title}</h4>
                  <p style={{ color: "#666", fontSize: "13px", lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section style={{ background: "#f4f4f2", padding: "72px 0" }}>
          <div style={{ maxWidth: "800px", margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
            <p style={sectionLabel}>{t("about.ourStory")}</p>
            <h2 style={{ ...sectionH2, margin: "0 0 24px" }}>{t("about.storyTitle")}</h2>
            <p style={{ color: "#555", lineHeight: 1.9, fontSize: "16px", marginBottom: "20px" }}>
              {t("about.storyP1")}
            </p>
            <p style={{ color: "#555", lineHeight: 1.9, fontSize: "16px", marginBottom: "20px" }}>
              {t("about.storyP2")}
            </p>
            <p style={{ color: "#555", lineHeight: 1.9, fontSize: "16px" }}>
              {t("about.storyP3")}
            </p>
          </div>
        </section>

        {/* Partnerships */}
        <section style={{ background: "#0e3725", padding: "64px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
            <p style={{ ...sectionLabel, color: "#c9a84c" }}>{t("about.partnershipsLabel")}</p>
            <h2 style={{ fontFamily: "inherit", fontSize: "clamp(26px, 3vw, 36px)", color: "#fff", margin: "0 0 48px", fontWeight: 700 }}>
              {t("about.partnershipsTitle")}
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "24px" }}>
              {partnerships.map((p, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "12px", padding: "32px 24px" }}>
                  <div style={{ marginBottom: "12px" }}>
                    <p.Ic size={36} color="#c9a84c" />
                  </div>
                  <h4 style={{ color: "#fff", fontSize: "16px", fontWeight: 700, margin: "0 0 8px" }}>{p.name}</h4>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", margin: 0 }}>{p.role}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership */}
        <section style={{ background: "#fff", padding: "72px 0" }}>
          <div style={{ maxWidth: "900px", margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
            <p style={sectionLabel}>{t("about.leadershipLabel")}</p>
            <h2 style={{ ...sectionH2, marginBottom: "48px" }}>{t("about.leadershipTitle")}</h2>
            <div style={{ display: "flex", justifyContent: "center", gap: "40px", flexWrap: "wrap" }}>
              {[
                { name: "Faisal Alferdos", role: t("about.ceoRole"),
                  desc: t("about.ceoDesc"),
                  bg: "linear-gradient(135deg, #0e3725, #1a5c3a)", Ic: IcUser },
                { name: t("about.ctoName"),  role: t("about.ctoRole"),
                  desc: t("about.ctoDesc"),
                  bg: "linear-gradient(135deg, #c9a84c, #a8873a)", Ic: IcCode },
              ].map((member, i) => (
                <div key={i} style={{ background: "#f4f4f2", borderRadius: "16px", padding: "40px 32px", maxWidth: "280px", textAlign: "center", border: "1px solid rgba(14,55,37,0.08)" }}>
                  <div style={{ width: "80px", height: "80px", borderRadius: "50%", background: member.bg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                    <member.Ic size={32} color="#fff" />
                  </div>
                  <h3 style={{ color: "#0e3725", fontSize: "20px", fontWeight: 700, margin: "0 0 6px" }}>{member.name}</h3>
                  <p style={{ color: "#c9a84c", fontSize: "13px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", margin: "0 0 12px" }}>{member.role}</p>
                  <p style={{ color: "#666", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section style={{ background: "#f4f4f2", padding: "72px 0" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "0 24px" }}>
            <div style={{ textAlign: "center", marginBottom: "48px" }}>
              <p style={sectionLabel}>{t("about.contactLabel")}</p>
              <h2 style={sectionH2}>{t("about.contactTitle")}</h2>
              <p style={{ color: "#666", fontSize: "16px", maxWidth: "500px", margin: "0 auto" }}>
                {t("about.contactSubtitle")}
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: isMobile ? "32px" : "48px", alignItems: "start" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {contactItems.map((item, i) => (
                  <div key={i} style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                    <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "#0e3725", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <item.Ic size={22} color="#c9a84c" />
                    </div>
                    <div>
                      <p style={{ color: "#999", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", margin: "0 0 4px" }}>{item.label}</p>
                      <p style={{ color: "#333", fontSize: "15px", margin: 0, lineHeight: 1.5 }}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: "#fff", borderRadius: "16px", padding: "36px", border: "1px solid rgba(14,55,37,0.08)", boxShadow: "0 4px 24px rgba(14,55,37,0.06)" }}>
                <h3 style={{ color: "#0e3725", fontSize: "20px", fontWeight: 700, margin: "0 0 24px" }}>{t("contact.sendMessage")}</h3>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const data = new FormData(e.target);
                  window.location.href = `mailto:partnerships@treasury.sa?subject=Inquiry from ${data.get('name')}&body=${encodeURIComponent(data.get('message') + '\n\nFrom: ' + data.get('name') + '\nEmail: ' + data.get('email'))}`;
                }}>
                  {[
                    { name: "name",  label: t("contact.name"),      type: "text",  placeholder: t("contact.namePlaceholder") },
                    { name: "email", label: t("contact.email"),   type: "email", placeholder: t("contact.emailPlaceholder") },
                  ].map((field, i) => (
                    <div key={i} style={{ marginBottom: "16px" }}>
                      <label style={{ display: "block", color: "#555", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>{field.label}</label>
                      <input name={field.name} type={field.type} required placeholder={field.placeholder} style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e0e0e0", borderRadius: "8px", fontSize: "15px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div style={{ marginBottom: "24px" }}>
                    <label style={{ display: "block", color: "#555", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>{t("contact.message")}</label>
                    <textarea name="message" required rows={4} placeholder={t("contact.messagePlaceholder")} style={{ width: "100%", padding: "12px 14px", border: "1.5px solid #e0e0e0", borderRadius: "8px", fontSize: "15px", outline: "none", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
                  </div>
                  <button type="submit" style={{ width: "100%", padding: "14px", background: "#0e3725", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.5px" }}>
                    {t("contact.sendMessage")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </div>
  );
};

export default memo(About);
